/**
 * JWT Utility Functions
 * 
 * Handles proper JWT token decoding with base64URL support.
 * JWTs use base64URL encoding (with `-` and `_` instead of `+` and `/`),
 * but `atob()` only handles standard base64, so we need to convert first.
 */

/**
 * Converts base64URL to standard base64 for decoding
 * JWT tokens use base64URL encoding which replaces:
 * - `+` with `-`
 * - `/` with `_`
 * - Removes padding `=`
 */
function base64URLToBase64(base64URL: string): string {
  // Replace URL-safe characters with standard base64 characters
  let base64 = base64URL.replace(/-/g, '+').replace(/_/g, '/');
  
  // Add padding if needed (base64 strings must be multiples of 4)
  while (base64.length % 4) {
    base64 += '=';
  }
  
  return base64;
}

/**
 * Decodes a JWT token payload
 * @param token - The full JWT token (header.payload.signature)
 * @returns The decoded payload object, or null if decoding fails
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function decodeJWTPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[decodeJWTPayload] Invalid JWT structure: expected 3 parts, got', parts.length);
      return null;
    }
    
    // Decode the payload (second part)
    const base64URL = parts[1];
    const base64 = base64URLToBase64(base64URL);
    const decoded = atob(base64);
    const payload = JSON.parse(decoded);
    
    return payload;
  } catch (error) {
    console.error('[decodeJWTPayload] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extracts a specific claim from a JWT token
 * @param token - The full JWT token
 * @param claimName - The name of the claim to extract
 * @returns The claim value, or null if not found or decoding fails
 */
export function getJWTClaim(token: string, claimName: string): unknown {
  const payload = decodeJWTPayload(token);
  return payload ? payload[claimName] ?? null : null;
}

